import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_AUDITS } from "../apis/settingsApis/audit-apis/auditApis";
import { GET_USER_DETAILS } from "../apis/dashboard-apis/dashboardDetailApi";
import Role from "../utils/roles";

/**
 * Hook to manage audit context and determine effective date filtering
 *
 * @param {Object} options - Configuration options
 * @param {string} options.auditId - Optional audit ID (if not provided, will try to extract from route)
 * @param {string} options.userRole - Optional user role (if not provided, will try to get from user context)
 * @returns {Object} - { audit, auditStartDate, auditEndDate, shouldFilter, isAuditor, loading }
 */
export const useAuditContext = (options = {}) => {
  const { auditId: providedAuditId, userRole: providedUserRole } = options;
  const params = useParams();
  const location = useLocation();
  
  // Try to get audit ID from various sources
  const auditId = useMemo(() => {
    if (providedAuditId) return providedAuditId;
    if (params.auditId) return params.auditId;
    // Try to extract from location state or query params
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("auditId") || null;
  }, [providedAuditId, params.auditId, location]);

  // Fetch audit details if auditId is available
  const { data: auditsData, loading } = useQuery(GET_AUDITS, {
    variables: {
      filter: auditId && auditId !== "0" ? { id: auditId } : {},
    },
    skip: !auditId || auditId === "0",
    fetchPolicy: "cache-and-network",
  });

  // Extract audit from query result
  const audit = useMemo(() => {
    if (!auditsData?.getAudits?.data || auditsData.getAudits.data.length === 0) {
      return null;
    }
    return auditsData.getAudits.data[0];
  }, [auditsData]);

  // Get user details to determine role
  const { data: userData } = useQuery(GET_USER_DETAILS, {
    skip: !!providedUserRole, // Skip if role is provided
    fetchPolicy: "cache-first",
  });

  // Determine user role
  const isAuditor = useMemo(() => {
    if (providedUserRole) {
      return providedUserRole === Role.AUDITOR;
    }
    // Get from user context
    const userDetails = userData?.getAccountDetails?.userDetails || {};
    return userDetails?.roles?.some(
      (role) => role?.role_description === Role.AUDITOR
    ) || false;
  }, [providedUserRole, userData]);

  // Determine effective date filter - always apply audit window filtering
  const {
    auditStartDate,
    auditEndDate,
    shouldFilter,
  } = useMemo(() => {
    if (!audit || !audit.audit_start_date || !audit.audit_end_date) {
      return {
        auditStartDate: null,
        auditEndDate: null,
        shouldFilter: false,
      };
    }

    const startDate = audit.audit_start_date;
    const endDate = audit.audit_end_date;

    // Always apply audit window filtering
    return {
      auditStartDate: startDate,
      auditEndDate: endDate,
      shouldFilter: true,
    };
  }, [audit]);

  return {
    audit,
    auditId,
    auditStartDate,
    auditEndDate,
    shouldFilter,
    isAuditor,
    loading,
  };
};
